import {css} from 'styled-components';

import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {makeSizeConstants} from '../../../../../libs/shared/ui/src/styles/styleHelpers';
import {boxShadow} from '../../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {AccountModel} from '../../../../models/api/accountModel';
import {AvatarCellModel} from '../cells/avatarCell';
import {virtualTableRowBorderRadius} from './tableHelpers';

/*
 * Constants.
 */

const normalAvatarSizes = makeSizeConstants(16, 24, 40);
const normalAvatarIconSizes = makeSizeConstants(8, 12, 20);
const defaultNonDenseSize: VisualSizesEnum = VisualSizesEnum.MEDIUM;

const smallAvatarSizes = makeSizeConstants(16, 20, 24);
const smallAvatarIconSizes = makeSizeConstants(8, 12, 14);
const defaultSmallSize: VisualSizesEnum = VisualSizesEnum.SMALL;

/*
 * Helpers.
 */

export const getTableCellAvatarSize = (
  size: VisualSizesEnum | undefined,
  avatarSize: VisualSizesEnum | undefined,
) => {
  if (size === VisualSizesEnum.SMALL) {
    return smallAvatarSizes[avatarSize ?? defaultSmallSize];
  }
  return normalAvatarSizes[avatarSize ?? defaultNonDenseSize];
};

export const getTableCellAvatarIconSize = (
  size: VisualSizesEnum | undefined,
  avatarSize: VisualSizesEnum | undefined,
) => {
  if (size === VisualSizesEnum.SMALL) {
    return smallAvatarIconSizes[avatarSize ?? defaultSmallSize];
  }
  return normalAvatarIconSizes[avatarSize ?? defaultNonDenseSize];
};

export function getCellBackgroundColor(isHovering?: boolean, isSelected?: boolean) {
  if (isSelected) {
    return css`
      background: ${(p) => p.theme.palette.blue.shade40};
    `;
  }

  if (isHovering) {
    return css`
      background: ${(p) => p.theme.greys.shade10};
    `;
  }
  return css`
    background: ${(p) => p.theme.foreground};
  `;
}

export function getCellTextColor(isSelected?: boolean, overrideDefaultUnselectedColor?: string) {
  return css`
    color: ${(p) => {
      const defaultColor = overrideDefaultUnselectedColor ?? p.theme.greys.shade80;
      return isSelected ? p.theme.invertedText : defaultColor;
    }};
  `;
}

export function getCellBoxShadowStyles(shouldShowShadow: boolean) {
  if (!shouldShowShadow) {
    return '';
  }
  return css`
    ${(p) => boxShadow(p.theme, `0px 1px 9px rgba(0, 0, 0, 0.08)`)};
    clip-path: inset(0px -15px 0px 0px);
  `;
}

export function getCellIndexStyles(
  index: number,
  isSelected?: boolean,
  shouldRemoveTopBorderRadius?: boolean,
  shouldRemoveBottomBorderRadius?: boolean,
  shouldRemoveLeftBorderRadius?: boolean,
  isOnlyColumn?: boolean,
) {
  if (isSelected && shouldRemoveTopBorderRadius && shouldRemoveBottomBorderRadius) {
    return null;
  }
  return css`
    border-radius: ${virtualTableRowBorderRadius}px;
    // Disable border radius for selected rows.
    ${isSelected &&
    shouldRemoveTopBorderRadius &&
    css`
      border-top-left-radius: 0;
      border-top-right-radius: 0;
    `}
    ${isSelected &&
    shouldRemoveBottomBorderRadius &&
    css`
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    `}
    ${shouldRemoveLeftBorderRadius &&
    css`
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    `}
    // TODO: this only supports the first column being sticky, if we need support for more columns this needs to be reworked.
    ${index === 0 &&
    !isOnlyColumn &&
    css`
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    `}
  `;
}

export function convertAccountToAvatarCellModel(account: AccountModel): AvatarCellModel {
  return {
    avatarUrl: account.avatarUrl,
    avatarIcon: 'briefcaseFilled',
    name: account.name,
  };
}

export function getCellPointerEventsStyle(isDisabled?: boolean) {
  if (isDisabled) {
    return css`
      pointer-events: none;
    `;
  }
  return '';
}
